import axios from 'axios'
import jwtService from './jwt.service'
import { createUrl } from '@/utils/helpers'
import environment from '../../environment'

export interface LogQuery {
  assetType?: string
  startDate?: string
  endDate?: string
  level?: string
  logLevel?: string
  userId?: number
  assetId?: string
  action?: string
  search?: string
  topic?: string
  organizationId?: number
  clientId?: string // Device ID for filtering by device
  skip?: number
  limit?: number
  page?: number
  pageSize?: number
  orderBy?: string
  orderDir?: string
}

export interface LogEntry {
  id: number
  timestamp: string
  level: string
  action: string
  message: string
  userId: number | null
  userEmail: string | null
  assetId: string | null
  clientId: string | null // Device ID
  ipAddress: string | null
  userAgent: string | null
  requestMethod: string | null
  requestPath: string | null
  responseStatus: number | null
  responseTimeMs: number | null
  extraData: Record<string, unknown> | null
}

export interface LogResponse {
  total?: number
  skip?: number
  limit?: number
  logs?: LogEntry[]
  items?: LogEntry[]
}

export interface LogStats {
  total: number
  byLevel: Record<string, number>
  byAction: Record<string, number>
  byHour: Array<{ hour: number; count: number }>
}

export interface DeviceDetails {
  id: number
  clientId: string | null
  clientName: string | null
  deviceId: string | null
  chipsetId: string | null
  label: string | null
  jetpack: string | null
  cordatusVersion: string | null
  natType: string | null
  locationType: string | null
  url: string | null
  deviceParams: Record<string, unknown> | null
  userId: number | null
  userName: string | null
  userEmail: string | null
  issuedAt: string | null
  deleted: boolean
  hasTunnel: boolean
  tunnelUrl: string | null
}

// Create axios instance for log service - routes through admin backend
export const logApiService = axios.create({
  baseURL: createUrl(
    environment.backendSecure,
    environment.backendUrl,
    environment.backendPort
  ),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Token ${jwtService.getToken()}`,
    'Access-Control-Allow-Origin': '*'
  }
})

// Interceptor to update token on each request
logApiService.interceptors.request.use((config) => {
  const token = jwtService.getToken()
  if (token) {
    config.headers.Authorization = `Token ${token}`
  }
  return config
})

export const logService = {
  /**
       * Get logs with optional filters
       */
  async getLogs (params: LogQuery | Record<string, unknown>): Promise<LogResponse> {
    const queryParams = new URLSearchParams()
    const p = params as LogQuery
    const assetType = p.assetType || 'general'

    // Add asset_type to query params
    queryParams.append('asset_type', assetType)

    if (p.startDate) {
      queryParams.append('start_date', p.startDate)
    }
    if (p.endDate) {
      queryParams.append('end_date', p.endDate)
    }
    if (p.level && p.level !== 'all') {
      queryParams.append('log_level', p.level)
    }
    if (p.logLevel) {
      queryParams.append('log_level', p.logLevel)
    }
    if (p.userId) {
      queryParams.append('user_id', p.userId.toString())
    }
    if (p.assetId) {
      queryParams.append('asset_id', p.assetId)
    }
    if (p.action) {
      queryParams.append('action', p.action)
    }
    if (p.search) {
      queryParams.append('search', p.search)
    }
    if (p.topic) {
      queryParams.append('topic', p.topic)
    }
    if (p.organizationId) {
      queryParams.append('organization_id', p.organizationId.toString())
    }
    if (p.clientId) {
      queryParams.append('client_id', p.clientId)
    }
    if (p.skip !== undefined) {
      queryParams.append('skip', p.skip.toString())
    }
    if (p.limit !== undefined) {
      queryParams.append('limit', p.limit.toString())
    }
    if (p.page !== undefined) {
      queryParams.append('page', p.page.toString())
    }
    if (p.pageSize !== undefined) {
      queryParams.append('page_size', p.pageSize.toString())
    }
    if (p.orderBy) {
      queryParams.append('order_by', p.orderBy)
    }
    if (p.orderDir) {
      queryParams.append('order_dir', p.orderDir)
    }

    // Use admin backend endpoint
    const response = await logApiService.get(`/logs/query?${queryParams.toString()}`)
    return response.data
  },

  /**
       * Get logs for a specific asset type
       */
  async getLogsByAsset (assetType: string, params: LogQuery): Promise<LogResponse> {
    const queryParams = new URLSearchParams()
    queryParams.append('asset_type', assetType)

    if (params.startDate) {
      queryParams.append('start_date', params.startDate)
    }
    if (params.endDate) {
      queryParams.append('end_date', params.endDate)
    }
    if (params.level && params.level !== 'all') {
      queryParams.append('log_level', params.level)
    }
    if (params.userId) {
      queryParams.append('user_id', params.userId.toString())
    }
    if (params.assetId) {
      queryParams.append('asset_id', params.assetId)
    }
    if (params.skip !== undefined) {
      queryParams.append('skip', params.skip.toString())
    }
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString())
    }

    // Use admin backend endpoint
    const response = await logApiService.get(`/logs/query?${queryParams.toString()}`)
    return response.data
  },

  /**
       * Get log statistics
       */
  async getStats (params: { assetType?: string; startDate?: string; endDate?: string; clientId?: string } | Record<string, unknown>): Promise<LogStats> {
    const queryParams = new URLSearchParams()
    const p = params as { assetType?: string; startDate?: string; endDate?: string; clientId?: string }

    if (p.assetType && p.assetType !== 'all') {
      queryParams.append('asset_type', p.assetType)
    }
    if (p.startDate) {
      queryParams.append('start_date', p.startDate)
    }
    if (p.endDate) {
      queryParams.append('end_date', p.endDate)
    }
    if (p.clientId) {
      queryParams.append('client_id', p.clientId)
    }

    // Use admin backend endpoint
    const response = await logApiService.get(`/logs/stats?${queryParams.toString()}`)
    return response.data
  },

  /**
       * Get available asset types
       */
  async getAssetTypes (): Promise<string[]> {
    // Use admin backend endpoint
    const response = await logApiService.get('/logs/asset-types')
    return response.data.asset_types
  },

  /**
       * Get distinct devices/clients from logs
       */
  // eslint-disable-next-line camelcase
  async getDevices (assetType: string): Promise<{ client_id: string; log_count: number; last_seen: string | null }[]> {
    // Use admin backend endpoint
    const response = await logApiService.get(`/logs/devices?asset_type=${assetType}`)
    return response.data.devices || []
  },

  /**
       * Get detailed device information by client_id
       */
  async getDeviceDetails (clientId: string): Promise<DeviceDetails | null> {
    try {
      const response = await logApiService.get(`/logs/device/${clientId}`)
      return response.data
    } catch (error) {
      console.error('Failed to get device details:', error)
      return null
    }
  },

  /**
       * Health check for log service
       */
  async healthCheck (): Promise<{ status: string }> {
    // Health check still goes directly to log service for now
    const response = await logApiService.get('/health')
    return response.data
  },

  /**
       * Export logs to JSON format
       */
  async exportLogs (params: LogQuery): Promise<LogEntry[]> {
    // Get all logs without pagination for export
    const exportParams = { ...params, skip: 0, limit: 10000 }
    const response = await this.getLogs(exportParams)
    return response.logs || response.items || []
  }
}

export default logService
